import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Platform, useWindowDimensions } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from './ui';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { fontWeights } from '../styles/typographyTokens';

import { ON_ACCENT_TEXT } from '../styles/accentText';
interface SettingsPanelProps {
  navigation: any;
  onLogout: () => void;
}

type PrivacyLevel = 'public' | 'subscribers' | 'private';

const PRIVACY_LABELS: Record<PrivacyLevel, string> = {
  public: 'Все',
  subscribers: 'Подписчики',
  private: 'Только я',
};

interface TileProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  desc: string;
  rightLabel?: string;
  onPress?: () => void;
  isDark: boolean;
  tc: any;
  accent?: string;
}

const Tile: React.FC<TileProps> = ({ icon, title, desc, rightLabel, onPress, isDark, tc, accent }) => {
  const iconBg = accent
    ? `${accent}1A`
    : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)');
  const iconColor = accent || tc.text;
  return (
    <TouchableOpacity
      style={[s.tile, { backgroundColor: tc.surfacePrimary, borderColor: tc.border }]}
      onPress={onPress}
      activeOpacity={0.8}
      disabled={!onPress}
    >
      <View style={[s.tileIcon, { backgroundColor: iconBg }]}>
        <Ionicons name={icon} size={18} color={iconColor} />
      </View>
      <View style={s.tileBody}>
        <Typography variant="h5" style={[s.tileTitle, { color: accent || tc.text }] as any}>{title}</Typography>
        <Typography variant="body" style={[s.tileDesc, { color: tc.textMuted }] as any}>{desc}</Typography>
      </View>
      {rightLabel ? (
        <View style={[s.tilePill, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }]}>
          <Typography variant="caption" style={[s.tilePillText, { color: tc.textSecondary || tc.text }] as any}>{rightLabel}</Typography>
        </View>
      ) : onPress ? (
        <Ionicons name="chevron-forward" size={18} color={tc.textMuted} />
      ) : null}
    </TouchableOpacity>
  );
};

interface PrivacyTileProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  desc: string;
  value: PrivacyLevel;
  onChange: (next: PrivacyLevel) => void;
  isDark: boolean;
  tc: any;
}

/**
 * Privacy tile with an inline segmented selector instead of a click-to-cycle
 * pill. Owner reported the cycle was «не очень интуитивно понятно» — users
 * couldn't tell what the current value was without poking the pill, and
 * couldn't tell that pressing again would change it. Showing all three
 * options side-by-side with the active one filled in makes both the
 * current state and the available options self-evident.
 */
const PrivacyTile: React.FC<PrivacyTileProps> = ({ icon, title, desc, value, onChange, isDark, tc }) => {
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  const segmentBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const segmentBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const order: PrivacyLevel[] = ['public', 'subscribers', 'private'];
  return (
    <View style={[s.tile, { backgroundColor: tc.surfacePrimary, borderColor: tc.border, alignItems: 'flex-start' }]}>
      <View style={[s.tileIcon, { backgroundColor: iconBg, marginTop: 2 }]}>
        <Ionicons name={icon} size={18} color={tc.text} />
      </View>
      <View style={[s.tileBody, { gap: 12 }]}>
        <View style={{ gap: 4 }}>
          <Typography variant="h5" style={[s.tileTitle, { color: tc.text }] as any}>{title}</Typography>
          <Typography variant="body" style={[s.tileDesc, { color: tc.textMuted }] as any}>{desc}</Typography>
        </View>
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: segmentBg,
            borderRadius: 999,
            padding: 3,
            borderWidth: 1,
            borderColor: segmentBorder,
            alignSelf: 'stretch',
          }}
        >
          {order.map((opt) => {
            const active = value === opt;
            return (
              <TouchableOpacity
                key={opt}
                onPress={() => onChange(opt)}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                activeOpacity={0.85}
                style={{
                  flex: 1,
                  paddingVertical: 7,
                  borderRadius: 999,
                  backgroundColor: active ? '#E98245' : 'transparent',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: active ? ON_ACCENT_TEXT : (tc.textSecondary || tc.text),
                    fontSize: 12,
                    fontWeight: active ? '800' : '600',
                    letterSpacing: 0.2,
                  } as any}
                >
                  {PRIVACY_LABELS[opt]}
                </Typography>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    </View>
  );
};

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ navigation, onLogout }) => {
  const { isDark, colors: tc, cycleMode } = useTheme() as any;
  const { user } = useAuth();
  const { width } = useWindowDimensions();
  const isWide = Platform.OS === 'web' && width >= 900;

  const [calendarPrivacy, setCalendarPrivacy] = useState<PrivacyLevel>('subscribers');
  const [feedPrivacy, setFeedPrivacy] = useState<PrivacyLevel>('public');

  const gridStyle = isWide
    ? ({ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 } as any)
    : ({ gap: 10 } as any);

  return (
    <View style={s.container}>
      <Typography variant="h2" style={[s.pageTitle, { color: tc.text }] as any}>
        Настройки
      </Typography>

      {/* Account identity — moved out of the Header avatar dropdown so
          the user can see (and eventually edit) their email exactly
          where they manage everything else about their account. */}
      {user?.email ? (
        <View style={[s.identityRow, { backgroundColor: tc.surfacePrimary, borderColor: tc.border }]}>
          <View style={[s.identityIcon, { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }]}>
            <Ionicons name="mail-outline" size={18} color={tc.text} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" style={[s.identityLabel, { color: tc.textMuted }] as any}>
              EMAIL
            </Typography>
            <Typography variant="body" style={[s.identityValue, { color: tc.text }] as any} numberOfLines={1}>
              {user.email}
            </Typography>
          </View>
        </View>
      ) : null}

      {/* АККАУНТ */}
      <View style={s.section}>
        <Typography variant="overline" style={[s.sectionTitle, { color: tc.textMuted }] as any}>АККАУНТ</Typography>
        <View style={gridStyle}>
          <Tile
            icon="notifications-outline" title="Уведомления"
            desc="Push и напоминания о событиях"
            onPress={() => navigation.navigate('NotificationSettings')}
            isDark={isDark} tc={tc}
          />
          <Tile
            icon="heart-outline" title="Избранное"
            desc="Избранные практики и материалы"
            onPress={() => navigation.navigate('Dashboard', { initialTab: 'favorites' })}
            isDark={isDark} tc={tc}
          />
          <Tile
            icon={isDark ? 'moon-outline' : 'sunny-outline'} title="Тема"
            desc="Оформление интерфейса"
            rightLabel={isDark ? 'Тёмная' : 'Светлая'}
            onPress={typeof cycleMode === 'function' ? cycleMode : undefined}
            isDark={isDark} tc={tc}
          />
        </View>
      </View>

      {/* ПРИВАТНОСТЬ */}
      <View style={s.section}>
        <Typography variant="overline" style={[s.sectionTitle, { color: tc.textMuted }] as any}>ПРИВАТНОСТЬ</Typography>
        <View style={gridStyle}>
          <PrivacyTile
            icon="calendar-outline" title="Календарь событий"
            desc="Кто видит, на какие события вы идёте"
            value={calendarPrivacy}
            onChange={setCalendarPrivacy}
            isDark={isDark} tc={tc}
          />
          <PrivacyTile
            icon="newspaper-outline" title="Практики в ленте"
            desc="Кто видит практики, добавленные в ленту"
            value={feedPrivacy}
            onChange={setFeedPrivacy}
            isDark={isDark} tc={tc}
          />
        </View>
      </View>

      {/* АККАУНТ И БЕЗОПАСНОСТЬ */}
      <View style={s.section}>
        <Typography variant="overline" style={[s.sectionTitle, { color: tc.textMuted }] as any}>АККАУНТ И БЕЗОПАСНОСТЬ</Typography>
        <View style={gridStyle}>
          <Tile
            icon="key-outline" title="Безопасность входа"
            desc="Passkey и пароль"
            onPress={() => navigation.navigate('SecuritySettings')}
            isDark={isDark} tc={tc}
          />
        </View>
      </View>

      {/* Logout */}
      <View style={s.logoutRow}>
        <TouchableOpacity
          style={[s.logoutBtn, { borderColor: 'rgba(239, 68, 68, 0.25)', backgroundColor: isDark ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.04)' }]}
          onPress={onLogout}
          activeOpacity={0.7}
        >
          <Ionicons name="log-out-outline" size={20} color="#EF4444" />
          <Typography variant="body" style={{ fontWeight: fontWeights.bold, color: '#EF4444', fontSize: 16, lineHeight: 22 } as any}>
            Выйти из аккаунта
          </Typography>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const s = StyleSheet.create({
  container: {
    paddingBottom: 40,
    maxWidth: 960,
    width: '100%',
    alignSelf: 'flex-start',
  } as any,
  section: {
    marginBottom: 32,
  },
  pageTitle: {
    fontSize: Platform.OS === 'web' ? 48 : 36,
    lineHeight: Platform.OS === 'web' ? 56 : 42,
    fontWeight: fontWeights.bold,
    letterSpacing: -1.2,
    marginBottom: 32,
  } as any,
  identityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 16,
    borderWidth: 1,
    borderRadius: 20,
    marginBottom: 28,
  } as any,
  identityIcon: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
  } as any,
  identityLabel: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
    letterSpacing: 1.6,
    marginBottom: 2,
    textTransform: 'uppercase',
  } as any,
  identityValue: {
    fontSize: 16,
    fontWeight: '600',
  } as any,
  sectionTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    letterSpacing: 1.8,
    marginBottom: 16,
    marginLeft: 4,
    textTransform: 'uppercase',
  } as any,
  tile: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderWidth: 1,
    borderRadius: 16,
    gap: 16,
    minWidth: 0,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
    } as any),
  } as any,
  tileIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as any,
  tileBody: {
    flex: 1,
    minWidth: 0,
  } as any,
  tileTitle: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    lineHeight: 24,
    letterSpacing: -0.2,
  } as any,
  tileDesc: {
    fontSize: 15,
    fontWeight: '400',
    marginTop: 4,
    lineHeight: 22,
  } as any,
  tilePill: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    flexShrink: 0,
  } as any,
  tilePillText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.4,
  } as any,
  logoutRow: {
    marginTop: 8,
    alignItems: 'flex-start',
  } as any,
  logoutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 22,
    paddingVertical: 12,
    borderRadius: 999,
    borderWidth: 1,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  } as any,
});
